import { useDebounce } from 'react-use';
import styled from 'styled-components';

import {
   PasswordExpirationSettings,
   useUpdatePasswordExpirationSettingsCommand,
} from '@dreryk/egabinet-security-frontend';
import { Translate } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent, Toggle } from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../translations';

import { PasswordExpirationSettingsController } from './PasswordExpirationSettings';
import { PasswordExpirationSettingsFormBean } from './PasswordExpirationSettings.types';

type DoNotEnforcePasswordChangeFieldProps = {
   readonly register: PasswordExpirationSettingsController['register'];
   readonly isDirty: boolean;
   readonly value: boolean | null;
};

const DO_NOT_ENFORCE_PASSWORD_CHANGE_FIELD_DEBOUNCE_IS_MS = 500;

const StyledDoNotEnforcePasswordChangeField = styled.div`
   display: flex;
   gap: 0.5em;
`;
StyledDoNotEnforcePasswordChangeField.displayName = 'StyledDoNotEnforcePasswordChangeField';

const DoNotEnforcePasswordChangeField = (props: DoNotEnforcePasswordChangeFieldProps): JSX.Element => {
   const {
      loading: updatePasswordExpirationSettingsLoading,
      execute: updatePasswordExpirationSettings,
   } = useUpdatePasswordExpirationSettingsCommand((settings: PasswordExpirationSettings): void => {
      dispatchSnackbarPositiveEvent(
         settings.doNotEnforcePasswordChange
            ? personnelTranslationKeys.myAccount.password.passwordUpdateNotEnforced
            : personnelTranslationKeys.myAccount.password.passwordUpdateEnforcedEveryThirtyDays
      );
   });

   useDebounce(
      () => {
         if (!updatePasswordExpirationSettingsLoading && props.isDirty) {
            updatePasswordExpirationSettings({ doNotEnforcePasswordChange: props.value! });
         }
      },
      DO_NOT_ENFORCE_PASSWORD_CHANGE_FIELD_DEBOUNCE_IS_MS,
      [props.value]
   );

   return (
      <StyledDoNotEnforcePasswordChangeField>
         <Toggle
            testId={'doNotEnforcePasswordChange'}
            controller={props.register(DoNotEnforcePasswordChangeField.fieldName)}
            rightSlot={<Translate>{personnelTranslationKeys.myAccount.password.doNotEnforcePasswordChange}</Translate>}
            horizontal
         />
      </StyledDoNotEnforcePasswordChangeField>
   );
};

DoNotEnforcePasswordChangeField.fieldName = 'doNotEnforcePasswordChange' as keyof PasswordExpirationSettingsFormBean;

export { DoNotEnforcePasswordChangeField };
